import { useState, useMemo } from 'react';
import { 
  TrendingUp, TrendingDown, Wallet, Home, Banknote, Wrench,
  BarChart3, Sparkles, Info, AlertCircle, CheckCircle2, ArrowUpRight,
  ArrowDownRight, Euro, Building, Calculator, Briefcase, Award, ThumbsUp, ThumbsDown, Users
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, Legend, RadialBarChart, RadialBar
} from 'recharts';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const formatEUR = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.round(n || 0));
const formatPct = (n) => `${(n || 0).toFixed(1)}%`;

const EditableField = ({ label, value, onChange, suffix = '€', icon: Icon, hint }) => (
  <div className="bg-white rounded-xl p-4 border border-gray-200 hover:border-[#c9a227]/50 transition-colors group">
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2 text-gray-500 text-xs font-medium uppercase tracking-wider">
        {Icon && <Icon size={14} />}
        {label}
      </div>
      {hint && (
        <span className="text-[10px] text-gray-400">{hint}</span>
      )}
    </div>
    <div className="flex items-baseline gap-1">
      <input
        type="number"
        value={value || 0}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="w-full text-2xl font-bold text-[#1e3a5f] bg-transparent border-0 focus:outline-none focus:ring-0 p-0 tabular-nums"
        data-testid={`investor-input-${label.toLowerCase().replace(/\s+/g, '-')}`}
      />
      <span className="text-gray-400 text-sm font-medium">{suffix}</span>
    </div>
  </div>
);

const MetricCard = ({ label, value, sublabel, trend, color = 'navy', icon: Icon, large }) => {
  const colors = {
    navy: 'from-[#1e3a5f] to-[#0f1d2f] text-white border-transparent',
    gold: 'from-[#c9a227] to-[#a8861e] text-white border-transparent',
    light: 'bg-white text-[#1e3a5f] border-gray-200',
    green: 'from-emerald-500 to-emerald-700 text-white border-transparent',
    red: 'from-red-500 to-red-700 text-white border-transparent',
  };
  const isGradient = ['navy', 'gold', 'green', 'red'].includes(color);
  return (
    <div className={`${isGradient ? `bg-gradient-to-br ${colors[color]}` : colors[color]} ${large ? 'col-span-2' : ''} rounded-2xl p-5 border relative overflow-hidden`}>
      {/* Decorative blur */}
      <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-semibold uppercase tracking-wider ${isGradient ? 'opacity-80' : 'text-gray-500'}`}>
            {label}
          </span>
          {Icon && <Icon size={16} className={isGradient ? 'opacity-70' : 'text-gray-400'} />}
        </div>
        <p className={`${large ? 'text-4xl md:text-5xl' : 'text-2xl md:text-3xl'} font-bold tabular-nums leading-none mb-1`}>
          {value}
        </p>
        {sublabel && (
          <p className={`text-xs mt-2 ${isGradient ? 'opacity-75' : 'text-gray-500'} flex items-center gap-1`}>
            {trend === 'up' && <ArrowUpRight size={12} className="text-emerald-300" />}
            {trend === 'down' && <ArrowDownRight size={12} className="text-red-300" />}
            {sublabel}
          </p>
        )}
      </div>
    </div>
  );
};

const InsightCard = ({ type = 'info', icon: Icon, title, children }) => {
  const styles = {
    success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    warning: 'bg-amber-50 border-amber-200 text-amber-900',
    info: 'bg-blue-50 border-blue-200 text-blue-900',
    gold: 'bg-[#c9a227]/10 border-[#c9a227]/30 text-[#1e3a5f]',
  };
  const iconColors = {
    success: 'text-emerald-600',
    warning: 'text-amber-600',
    info: 'text-blue-600',
    gold: 'text-[#c9a227]',
  };
  return (
    <div className={`rounded-xl border p-4 ${styles[type]}`}>
      <div className="flex items-start gap-3">
        {Icon && <Icon className={`flex-shrink-0 mt-0.5 ${iconColors[type]}`} size={18} />}
        <div className="flex-1">
          {title && <p className="font-semibold text-sm mb-1">{title}</p>}
          <p className="text-sm leading-relaxed">{children}</p>
        </div>
      </div>
    </div>
  );
};

const InvestorReport = ({ property }) => {
  // Editable state — initialized from property data (with admin pre-fills)
  const defaultMarketPricePerSqm = property.market_price_per_sqm 
    || (property.price && property.surface ? Math.round(property.price / property.surface * 1.05) : 4000);
  const colocationAvailable = property.colocation_active && property.surface >= 80;
  const defaultRoomCount = property.room_count || (property.surface >= 80 ? Math.max(2, Math.floor(property.surface / 25)) : 0);
  const defaultAirbnb = property.airbnb_monthly_est 
    || (property.estimated_rent ? Math.round(property.estimated_rent * 1.7) : 0);

  const [inputs, setInputs] = useState({
    surface: property.surface || 0,
    price: property.price || 0,
    monthlyRent: property.estimated_rent || 0,
    works: property.default_works || 0,
    marketPricePerSqm: defaultMarketPricePerSqm,
    airbnbMonthly: defaultAirbnb,
    agencyFee: 8,
    pricePerRoom: property.price_per_room || 0,
    roomCount: defaultRoomCount,
  });

  // Active rental strategy selector
  const [activeStrategy, setActiveStrategy] = useState('classique'); // 'classique' | 'airbnb' | 'colocation'

  // Interactive chart legend — track hidden series
  const [hiddenSeries, setHiddenSeries] = useState(new Set());
  const toggleSeries = (key) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const update = (key) => (val) => setInputs((s) => ({ ...s, [key]: val }));

  // Derived: effective monthly rent based on active strategy
  const effectiveMonthlyRent = useMemo(() => {
    if (activeStrategy === 'airbnb') return Math.round(inputs.airbnbMonthly * 0.50); // net occupancy 50%
    if (activeStrategy === 'colocation' && colocationAvailable) return Math.round(inputs.pricePerRoom * inputs.roomCount * 0.95);
    return inputs.monthlyRent;
  }, [activeStrategy, inputs, colocationAvailable]);

  const metrics = useMemo(() => {
    const { surface, price, monthlyRent, works, marketPricePerSqm, airbnbMonthly, agencyFee, pricePerRoom, roomCount } = inputs;
    const totalInvestment = price + works;
    const pricePerSqm = surface > 0 ? price / surface : 0;
    const marketDiff = marketPricePerSqm > 0 ? ((pricePerSqm - marketPricePerSqm) / marketPricePerSqm) * 100 : 0;
    const annualRent = monthlyRent * 12;
    const classiqueRoi = totalInvestment > 0 ? (annualRent / totalInvestment) * 100 : 0;
    const priceRentRatio = monthlyRent > 0 ? price / monthlyRent : 0;
    const agencyMonthly = monthlyRent * (agencyFee / 100);
    // Airbnb: occupancy 50% net of fees + cleaning
    const airbnbAnnualNet = airbnbMonthly * 12 * 0.50;
    const airbnbRoi = totalInvestment > 0 ? (airbnbAnnualNet / totalInvestment) * 100 : 0;
    const airbnbMonthlyNet = airbnbAnnualNet / 12;
    // Colocation
    const colocationMonthly = pricePerRoom * roomCount;
    const colocationAnnual = colocationMonthly * 12 * 0.95;
    const colocationRoi = totalInvestment > 0 ? (colocationAnnual / totalInvestment) * 100 : 0;

    // Active strategy → dynamic ROI brut
    const grossRoi = activeStrategy === 'airbnb' ? airbnbRoi
      : activeStrategy === 'colocation' && colocationAvailable ? colocationRoi
      : classiqueRoi;
    const activeAnnualRevenue = activeStrategy === 'airbnb' ? airbnbAnnualNet
      : activeStrategy === 'colocation' && colocationAvailable ? colocationAnnual
      : annualRent;

    // Charts data
    const comparisonData = [
      { name: 'Revenu annuel', Classique: Math.round(annualRent), Airbnb: Math.round(airbnbAnnualNet), ...(colocationAvailable && { Colocation: Math.round(colocationAnnual) }) },
      { name: 'ROI annuel', Classique: parseFloat(classiqueRoi.toFixed(1)), Airbnb: parseFloat(airbnbRoi.toFixed(1)), ...(colocationAvailable && { Colocation: parseFloat(colocationRoi.toFixed(1)) }) },
    ];

    // 10-year projection: 2% rent growth, 1.5% asset appreciation
    const projection = [];
    let cumulClassique = 0;
    let cumulAirbnb = 0;
    let cumulColocation = 0;
    let propertyValue = totalInvestment;
    for (let y = 0; y <= 10; y++) {
      const rentY = annualRent * Math.pow(1.02, y);
      const airbnbY = airbnbAnnualNet * Math.pow(1.02, y);
      const colocY = colocationAnnual * Math.pow(1.02, y);
      if (y > 0) {
        cumulClassique += rentY - 120 * 12 - agencyMonthly * 12;
        cumulAirbnb += airbnbY - 200 * 12;
        cumulColocation += colocY - 150 * 12;
        propertyValue *= 1.015;
      }
      projection.push({
        année: `An ${y}`,
        Classique: Math.round(cumulClassique),
        Airbnb: Math.round(cumulAirbnb),
        ...(colocationAvailable && { Colocation: Math.round(cumulColocation) }),
        Patrimoine: Math.round(propertyValue - totalInvestment),
      });
    }

    // Verdict logic — considers colocation if available
    let verdict;
    const airbnbAdvantage = annualRent > 0 ? ((airbnbAnnualNet - annualRent) / annualRent) * 100 : 0;
    const colocationAdvantage = annualRent > 0 && colocationAvailable ? ((colocationAnnual - annualRent) / annualRent) * 100 : 0;
    const bestStrategy = colocationAvailable && colocationAnnual > airbnbAnnualNet && colocationAnnual > annualRent
      ? 'colocation'
      : airbnbAdvantage > 30 && airbnbRoi > 7
      ? 'airbnb'
      : airbnbAdvantage < 10 ? 'classique' : 'mixte';

    if (bestStrategy === 'colocation') {
      verdict = {
        recommendation: 'colocation',
        title: 'Colocation recommandée',
        color: 'gold',
        icon: Users,
        score: Math.min(80 + Math.round(colocationAdvantage / 5), 96),
        reason: `La colocation génère +${colocationAdvantage.toFixed(0)}% de revenus vs location classique pour un ROI brut de ${colocationRoi.toFixed(1)}%. Le bien (${surface} m²) est parfait pour cette stratégie.`,
        caveat: 'Gestion par chambre, baux séparés ou bail collectif. Préférez le bail meublé pour optimiser fiscalement (LMNP).',
      };
    } else if (bestStrategy === 'airbnb') {
      verdict = {
        recommendation: 'airbnb',
        title: 'Location courte durée recommandée',
        color: 'gold',
        icon: ThumbsUp,
        score: Math.min(85 + Math.round(airbnbAdvantage / 5), 98),
        reason: `La courte durée génère +${airbnbAdvantage.toFixed(0)}% de revenus vs location classique pour un ROI net de ${airbnbRoi.toFixed(1)}%.`,
        caveat: 'Vérifiez la réglementation locale (autorisation mairie, quota Airbnb, taxe de séjour). Effort de gestion plus important.',
      };
    } else if (bestStrategy === 'classique') {
      verdict = {
        recommendation: 'classique',
        title: 'Location classique préférable',
        color: 'gold',
        icon: Home,
        score: 75,
        reason: `L'écart Airbnb (+${airbnbAdvantage.toFixed(0)}%) ne justifie pas l'effort supplémentaire. Optez pour la stabilité de la location longue durée.`,
        caveat: 'Régime LMNP recommandé pour l\'optimisation fiscale (amortissement).',
      };
    } else {
      verdict = {
        recommendation: 'mixte',
        title: 'Stratégie mixte ou saisonnière',
        color: 'gold',
        icon: Award,
        score: 70,
        reason: `Écart modéré (+${airbnbAdvantage.toFixed(0)}%). Possibilité d'optimiser selon la saison ou de combiner les deux modèles.`,
        caveat: 'Étudiez l\'attractivité touristique du quartier et la concurrence Airbnb locale.',
      };
    }

    return {
      totalInvestment,
      pricePerSqm,
      marketDiff,
      annualRent,
      classiqueRoi,
      grossRoi,
      activeAnnualRevenue,
      priceRentRatio,
      agencyMonthly,
      airbnbAnnualNet,
      airbnbRoi,
      airbnbMonthlyNet,
      airbnbAdvantage,
      colocationMonthly,
      colocationAnnual,
      colocationRoi,
      colocationAdvantage,
      comparisonData,
      projection,
      verdict,
    };
  }, [inputs, colocationAvailable, activeStrategy]);

  return (
    <div className="relative my-6 md:my-8" data-testid="investor-report">
      {/* Animated decorative orbs (peps !) */}
      <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#c9a227]/20 rounded-full blur-3xl pointer-events-none animate-pulse"></div>
      <div className="absolute top-1/3 -left-10 w-40 h-40 bg-emerald-500/15 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-10 right-10 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative bg-gradient-to-br from-white via-gray-50 to-white rounded-3xl border-2 border-[#c9a227]/30 p-6 md:p-8 shadow-2xl shadow-[#c9a227]/5 overflow-hidden">
        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a227] to-transparent"></div>
        
        {/* Bottom-right ribbon */}
        <div className="absolute -bottom-12 -right-12 w-48 h-48 bg-gradient-to-br from-[#c9a227]/10 to-transparent rounded-full pointer-events-none"></div>

        {/* Header — peppy */}
        <div className="flex items-start justify-between mb-6 md:mb-8 flex-wrap gap-3 relative">
          <div className="flex-1 min-w-[220px]">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#1e3a5f] to-[#0f1d2f] text-white text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full mb-3 shadow-lg shadow-[#1e3a5f]/30">
              <Sparkles size={12} className="text-[#c9a227] animate-pulse" />
              Rapport Investisseur
              <span className="bg-[#c9a227] text-white text-[9px] px-1.5 py-0.5 rounded-full">PRO</span>
            </div>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#1e3a5f] via-[#c9a227] to-[#1e3a5f] bg-clip-text text-transparent leading-tight">
              Analyse détaillée
            </h2>
            <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                Live
              </span>
              Tous les chiffres se recalculent en temps réel
            </p>
          </div>
          <div className="bg-gradient-to-br from-[#1e3a5f] to-[#0f1d2f] rounded-2xl px-5 py-3 border border-[#c9a227]/30 shadow-xl shadow-[#1e3a5f]/20 text-white">
            <p className="text-[10px] text-[#c9a227] uppercase tracking-wider font-bold">Investissement total</p>
            <p className="text-2xl font-bold tabular-nums">{formatEUR(metrics.totalInvestment)}</p>
          </div>
        </div>

      {/* Editable Inputs Grid */}
      <div className="mb-8">
        <p className="text-xs font-bold text-[#1e3a5f] uppercase tracking-[0.15em] mb-3 flex items-center gap-2 border-l-4 border-[#c9a227] pl-3">
          <Calculator size={14} className="text-[#c9a227]" />
          Paramètres
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <EditableField label="Surface" value={inputs.surface} onChange={update('surface')} suffix="m²" icon={Home} />
          <EditableField label="Prix" value={inputs.price} onChange={update('price')} suffix="€" icon={Euro} />
          
          {/* Smart Loyer field with strategy selector */}
          <div className="bg-gradient-to-br from-white to-amber-50/50 rounded-xl p-4 border-2 border-[#c9a227]/30 col-span-2 md:col-span-1 relative overflow-hidden">
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-[#c9a227]/10 rounded-full blur-2xl pointer-events-none"></div>
            <div className="relative">
              <div className="flex items-center gap-1.5 text-gray-500 text-xs font-medium uppercase tracking-wider mb-2">
                <Banknote size={14} className="text-[#c9a227]" />
                Loyer mensuel
              </div>
              {/* Strategy tabs */}
              <div className="flex gap-1 mb-2 bg-gray-100 rounded-lg p-0.5">
                <button
                  type="button"
                  onClick={() => setActiveStrategy('classique')}
                  className={`flex-1 text-[10px] font-semibold px-1.5 py-1 rounded-md transition-all ${
                    activeStrategy === 'classique' ? 'bg-[#1e3a5f] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="strategy-classique"
                >
                  Longue
                </button>
                <button
                  type="button"
                  onClick={() => setActiveStrategy('airbnb')}
                  className={`flex-1 text-[10px] font-semibold px-1.5 py-1 rounded-md transition-all ${
                    activeStrategy === 'airbnb' ? 'bg-[#c9a227] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="strategy-airbnb"
                >
                  Airbnb
                </button>
                {colocationAvailable && (
                  <button
                    type="button"
                    onClick={() => setActiveStrategy('colocation')}
                    className={`flex-1 text-[10px] font-semibold px-1.5 py-1 rounded-md transition-all ${
                      activeStrategy === 'colocation' ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid="strategy-colocation"
                  >
                    Coloc
                  </button>
                )}
              </div>
              <div className="flex items-baseline gap-1">
                {activeStrategy === 'classique' && (
                  <input
                    type="number"
                    value={inputs.monthlyRent || 0}
                    onChange={(e) => update('monthlyRent')(Number(e.target.value) || 0)}
                    className="w-full text-2xl font-bold text-[#1e3a5f] bg-transparent border-0 focus:outline-none p-0 tabular-nums"
                  />
                )}
                {activeStrategy === 'airbnb' && (
                  <input
                    type="number"
                    value={inputs.airbnbMonthly || 0}
                    onChange={(e) => update('airbnbMonthly')(Number(e.target.value) || 0)}
                    className="w-full text-2xl font-bold text-[#c9a227] bg-transparent border-0 focus:outline-none p-0 tabular-nums"
                  />
                )}
                {activeStrategy === 'colocation' && (
                  <span className="w-full text-2xl font-bold text-emerald-600 tabular-nums">
                    {(inputs.pricePerRoom * inputs.roomCount).toLocaleString('fr-FR')}
                  </span>
                )}
                <span className="text-gray-400 text-sm font-medium">€/mois</span>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                {activeStrategy === 'classique' && 'Longue durée — stable, peu d\'effort'}
                {activeStrategy === 'airbnb' && 'Courte durée — revenu mensuel brut'}
                {activeStrategy === 'colocation' && (
                  <>
                    <span className="font-semibold text-emerald-700">{inputs.roomCount} chambres × {inputs.pricePerRoom}€</span>
                    {' '}— modifiables ci-dessous
                  </>
                )}
              </p>
            </div>
          </div>

          <EditableField label="Enveloppe travaux" value={inputs.works} onChange={update('works')} suffix="€" icon={Wrench} />
          <EditableField label="Prix m² secteur" value={inputs.marketPricePerSqm} onChange={update('marketPricePerSqm')} suffix="€/m²" icon={BarChart3} hint="Marché local" />
          <EditableField label="Frais gestion" value={inputs.agencyFee} onChange={update('agencyFee')} suffix="%" icon={Briefcase} hint="Agence locale" />
          {colocationAvailable && (
            <>
              <EditableField label="Nombre chambres" value={inputs.roomCount} onChange={update('roomCount')} suffix="ch." icon={Users} />
              <EditableField label="Loyer / chambre" value={inputs.pricePerRoom} onChange={update('pricePerRoom')} suffix="€/mois" icon={Banknote} />
            </>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="mb-8">
        <p className="text-xs font-bold text-[#1e3a5f] uppercase tracking-[0.15em] mb-3 flex items-center gap-2 border-l-4 border-[#c9a227] pl-3">
          <TrendingUp size={14} className="text-[#c9a227]" />
          Indicateurs clés
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          <MetricCard 
            label="Prix au m²" 
            value={formatEUR(metrics.pricePerSqm)} 
            sublabel={`${metrics.marketDiff >= 0 ? '+' : ''}${formatPct(metrics.marketDiff)} vs marché`}
            trend={metrics.marketDiff > 0 ? 'up' : 'down'}
            color="light"
            icon={BarChart3}
          />
          {/* ROI brut card with info popover and active strategy */}
          <div className="bg-gradient-to-br from-[#c9a227] to-[#a8861e] text-white rounded-2xl p-5 border-transparent relative overflow-hidden">
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider opacity-90 flex items-center gap-1.5">
                  ROI brut
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="opacity-70 hover:opacity-100 transition-opacity" aria-label="Comprendre le ROI brut">
                        <Info size={13} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-4" side="bottom" align="center">
                      <h4 className="font-semibold text-[#1e3a5f] mb-2 flex items-center gap-2">
                        <TrendingUp size={16} className="text-[#c9a227]" />
                        Qu'est-ce que le ROI brut ?
                      </h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Le <strong>rendement locatif brut</strong> mesure la rentabilité annuelle de votre investissement.
                      </p>
                      <div className="bg-gray-50 rounded-lg p-3 mb-3 text-center">
                        <p className="text-xs text-gray-500 mb-1">Formule</p>
                        <p className="text-sm font-mono font-semibold text-[#1e3a5f]">
                          (Loyer annuel ÷ Prix) × 100
                        </p>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-xs">
                        <p className="font-semibold text-amber-900 mb-1">Calcul pour ce bien :</p>
                        <p className="text-amber-800 tabular-nums">
                          ({formatEUR(metrics.activeAnnualRevenue)} ÷ {formatEUR(metrics.totalInvestment)}) × 100 = <strong>{formatPct(metrics.grossRoi)}</strong>
                        </p>
                      </div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">À titre de comparaison :</p>
                      <ul className="space-y-1 text-xs">
                        <li className="flex justify-between py-1 border-b border-gray-100"><span className="text-gray-600">Livret A</span><span className="font-semibold">1,5%</span></li>
                        <li className="flex justify-between py-1 border-b border-gray-100"><span className="text-gray-600">Assurance-vie (fonds €)</span><span className="font-semibold">~2,5%</span></li>
                        <li className="flex justify-between py-1 border-b border-gray-100"><span className="text-gray-600">SCPI moyenne FR</span><span className="font-semibold">4,5 – 6%</span></li>
                        <li className="flex justify-between py-1 bg-[#c9a227]/10 px-2 rounded mt-2"><span className="text-[#1e3a5f] font-semibold">Ce bien</span><span className="font-bold text-[#c9a227]">{formatPct(metrics.grossRoi)}</span></li>
                      </ul>
                    </PopoverContent>
                  </Popover>
                </span>
                <TrendingUp size={16} className="opacity-70" />
              </div>
              <p className="text-3xl md:text-4xl font-bold tabular-nums leading-none mb-1">
                {formatPct(metrics.grossRoi)}
              </p>
              <p className="text-xs opacity-80 mt-2 flex items-center gap-1.5">
                <span className="inline-flex items-center gap-1 bg-white/15 backdrop-blur-md px-2 py-0.5 rounded-full font-semibold text-[10px] uppercase tracking-wider">
                  {activeStrategy === 'classique' && 'Longue durée'}
                  {activeStrategy === 'airbnb' && 'Airbnb'}
                  {activeStrategy === 'colocation' && 'Colocation'}
                </span>
              </p>
            </div>
          </div>
          <MetricCard 
            label="Ratio P/R" 
            value={metrics.priceRentRatio.toFixed(0)} 
            sublabel={metrics.priceRentRatio < 180 ? 'Excellent' : metrics.priceRentRatio < 220 ? 'Bon' : 'Élevé'}
            color="light"
            icon={Calculator}
          />
        </div>
      </div>

      {/* PATRIMOINE HIGHLIGHT — Retirement & long-term wealth */}
      <div className="mb-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-900 text-white p-6 md:p-8">
          {/* Decorative orbs */}
          <div className="absolute -top-10 -right-10 w-72 h-72 bg-emerald-300/20 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-emerald-400/15 rounded-full blur-3xl pointer-events-none"></div>
          {/* Pattern overlay */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.04] pointer-events-none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
            <defs>
              <pattern id="patPat" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#patPat)" />
          </svg>

          <div className="relative grid md:grid-cols-12 gap-6 items-center">
            {/* Left: big plus-value number */}
            <div className="md:col-span-4">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-[10px] font-bold uppercase tracking-[0.25em] px-3 py-1.5 rounded-full mb-3">
                <span className="w-1.5 h-1.5 bg-emerald-300 rounded-full animate-pulse"></span>
                Patrimoine constitué
              </div>
              <p className="text-xs uppercase tracking-wider opacity-80 mb-1">Plus-value à 10 ans</p>
              <p className="font-['Playfair_Display'] text-5xl md:text-6xl font-bold leading-none mb-2 tabular-nums" style={{ textShadow: '0 0 30px rgba(255,255,255,0.3)' }}>
                +{formatEUR(metrics.projection[10]?.Patrimoine || 0)}
              </p>
              <p className="text-base opacity-90">
                Soit <strong className="text-white">{formatEUR(metrics.totalInvestment + (metrics.projection[10]?.Patrimoine || 0))}</strong> de valeur totale
              </p>
            </div>

            {/* Middle: 1.5% appreciation badge */}
            <div className="md:col-span-3 flex flex-col items-center">
              <div className="bg-white/15 backdrop-blur-md rounded-2xl p-5 border border-white/20 text-center w-full">
                <p className="text-xs uppercase tracking-wider opacity-80 mb-1">Appréciation/an</p>
                <p className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold tabular-nums">+1,5%</p>
                <p className="text-[10px] opacity-70 mt-2">Marché FR moyenne longue</p>
                <div className="mt-4 pt-4 border-t border-white/20">
                  <p className="text-xs uppercase tracking-wider opacity-80 mb-1">+ Loyers cumulés</p>
                  <p className="font-bold text-xl tabular-nums">{formatEUR((metrics.projection[10]?.Classique || 0))}</p>
                  <p className="text-[10px] opacity-70 mt-0.5">10 ans (longue durée)</p>
                </div>
              </div>
            </div>

            {/* Right: 3 key benefits */}
            <div className="md:col-span-5 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur-md flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={18} />
                </div>
                <div>
                  <p className="font-semibold text-sm">Le loyer couvre le remboursement</p>
                  <p className="text-xs opacity-80 mt-0.5">Les locataires remboursent votre capital chaque mois</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur-md flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={18} />
                </div>
                <div>
                  <p className="font-semibold text-sm">Préparation retraite</p>
                  <p className="text-xs opacity-80 mt-0.5">Capital + revenus locatifs après remboursement</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur-md flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={18} />
                </div>
                <div>
                  <p className="font-semibold text-sm">Transmission optimisée</p>
                  <p className="text-xs opacity-80 mt-0.5">Patrimoine transmissible &amp; régime fiscal avantageux</p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-xs opacity-75 mt-6 text-center italic border-t border-white/15 pt-4">
            🏛️ Stratégie patrimoniale recommandée pour préparer sa retraite et transmettre un capital à ses proches.
          </p>
        </div>
      </div>

      {/* Scenarios: Location classique vs Airbnb (+ Colocation if available) */}
      <div className="mb-8">
        <p className="text-xs font-bold text-[#1e3a5f] uppercase tracking-[0.15em] mb-3 flex items-center gap-2 border-l-4 border-[#c9a227] pl-3">
          <Briefcase size={14} className="text-[#c9a227]" />
          Scénarios de location
        </p>
        <div className={`grid gap-4 ${colocationAvailable ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
          {/* Location longue durée */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-[#1e3a5f]/10 flex items-center justify-center">
                  <Home size={18} className="text-[#1e3a5f]" />
                </div>
                <h4 className="font-semibold text-[#1e3a5f]">Location classique</h4>
              </div>
              <span className="text-xs bg-[#1e3a5f]/10 text-[#1e3a5f] px-2 py-0.5 rounded-full font-semibold">LMNP</span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between border-b border-gray-100 py-1.5">
                <span className="text-gray-600">Loyer mensuel</span>
                <span className="font-semibold tabular-nums">{formatEUR(inputs.monthlyRent)}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 py-1.5">
                <span className="text-gray-600">Loyer annuel brut</span>
                <span className="font-semibold tabular-nums">{formatEUR(metrics.annualRent)}</span>
              </div>
              <div className="flex justify-between border-b border-gray-100 py-1.5">
                <span className="text-gray-600">Frais gestion ({inputs.agencyFee}%)</span>
                <span className="font-semibold text-red-500 tabular-nums">−{formatEUR(metrics.agencyMonthly)}/mois</span>
              </div>
              <div className="flex justify-between pt-2">
                <span className="font-semibold text-[#1e3a5f]">ROI brut</span>
                <span className="font-bold text-[#c9a227] text-lg tabular-nums">{formatPct(metrics.classiqueRoi)}</span>
              </div>
            </div>
          </div>

          {/* Airbnb */}
          <div className="bg-gradient-to-br from-[#1e3a5f] to-[#0f1d2f] rounded-2xl border border-[#c9a227]/20 p-5 text-white relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#c9a227]/20 rounded-full blur-3xl"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-[#c9a227]/20 flex items-center justify-center">
                    <Building size={18} className="text-[#c9a227]" />
                  </div>
                  <h4 className="font-semibold">Location courte durée</h4>
                </div>
                <span className="text-xs bg-[#c9a227]/20 text-[#c9a227] px-2 py-0.5 rounded-full font-semibold">Airbnb</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between border-b border-white/10 py-1.5">
                  <span className="text-gray-300">Revenu net annuel*</span>
                  <span className="font-semibold tabular-nums">{formatEUR(metrics.airbnbAnnualNet)}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 py-1.5">
                  <span className="text-gray-300">Revenu net/mois</span>
                  <span className="font-semibold tabular-nums">{formatEUR(metrics.airbnbMonthlyNet)}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 py-1.5">
                  <span className="text-gray-300">vs location classique</span>
                  <span className={`font-semibold tabular-nums ${metrics.airbnbAnnualNet > metrics.annualRent ? 'text-emerald-400' : 'text-red-400'}`}>
                    {metrics.annualRent > 0 ? `${metrics.airbnbAnnualNet > metrics.annualRent ? '+' : ''}${formatPct(((metrics.airbnbAnnualNet - metrics.annualRent) / metrics.annualRent) * 100)}` : '—'}
                  </span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="font-semibold">ROI net Airbnb</span>
                  <span className="font-bold text-[#c9a227] text-lg tabular-nums">{formatPct(metrics.airbnbRoi)}</span>
                </div>
              </div>
              <p className="text-[10px] text-gray-400 mt-3 italic">* Occupation 50% nette de charges &amp; ménage</p>
            </div>
          </div>

          {/* Colocation (conditionnel) */}
          {colocationAvailable && (
            <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-2xl border border-emerald-300/30 p-5 text-white relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-emerald-300/20 rounded-full blur-3xl"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                      <Users size={18} className="text-white" />
                    </div>
                    <h4 className="font-semibold">Colocation</h4>
                  </div>
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full font-semibold">Prix/chambre</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between border-b border-white/15 py-1.5">
                    <span className="opacity-90">Nombre de chambres</span>
                    <span className="font-semibold tabular-nums">{inputs.roomCount}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/15 py-1.5">
                    <span className="opacity-90">Loyer total/mois</span>
                    <span className="font-semibold tabular-nums">{formatEUR(metrics.colocationMonthly)}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/15 py-1.5">
                    <span className="opacity-90">vs location classique</span>
                    <span className="font-semibold tabular-nums">
                      {metrics.annualRent > 0 ? `${metrics.colocationAnnual > metrics.annualRent ? '+' : ''}${formatPct(((metrics.colocationAnnual - metrics.annualRent) / metrics.annualRent) * 100)}` : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2">
                    <span className="font-semibold">ROI brut colocation</span>
                    <span className="font-bold text-lg tabular-nums">{formatPct(metrics.colocationRoi)}</span>
                  </div>
                </div>
                <p className="text-[10px] opacity-70 mt-3 italic">Bail meublé LMNP recommandé. 5% vacance estimée.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Charts */}
      <div className="mb-8">
        <p className="text-xs font-bold text-[#1e3a5f] uppercase tracking-[0.15em] mb-3 flex items-center gap-2 border-l-4 border-[#c9a227] pl-3">
          <BarChart3 size={14} className="text-[#c9a227]" />
          Visualisation <span className="text-[10px] font-normal text-gray-400 normal-case tracking-normal ml-1">— Cliquez sur les légendes pour filtrer</span>
        </p>
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Bar chart: Classique vs Airbnb (+ Coloc) */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-lg transition-shadow">
            <h4 className="font-semibold text-[#1e3a5f] text-sm mb-1">Revenu annuel</h4>
            <p className="text-xs text-gray-500 mb-4">Comparatif des stratégies (€/an)</p>
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={[metrics.comparisonData[0]]} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip 
                    cursor={{ fill: 'rgba(201, 162, 39, 0.05)' }}
                    contentStyle={{ background: '#1e3a5f', border: 'none', borderRadius: 12, color: '#fff', fontSize: 12 }}
                    formatter={(value) => formatEUR(value)}
                  />
                  <Legend 
                    wrapperStyle={{ fontSize: 12, paddingTop: 8, cursor: 'pointer' }} 
                    onClick={(e) => toggleSeries(`bar-${e.dataKey}`)}
                    formatter={(value, entry) => (
                      <span style={{ 
                        color: hiddenSeries.has(`bar-${entry.dataKey}`) ? '#9ca3af' : '#1e3a5f',
                        textDecoration: hiddenSeries.has(`bar-${entry.dataKey}`) ? 'line-through' : 'none',
                        userSelect: 'none'
                      }}>{value}</span>
                    )}
                  />
                  {!hiddenSeries.has('bar-Classique') && <Bar dataKey="Classique" fill="#1e3a5f" radius={[8, 8, 0, 0]} />}
                  {!hiddenSeries.has('bar-Airbnb') && <Bar dataKey="Airbnb" fill="#c9a227" radius={[8, 8, 0, 0]} />}
                  {colocationAvailable && !hiddenSeries.has('bar-Colocation') && <Bar dataKey="Colocation" fill="#10b981" radius={[8, 8, 0, 0]} />}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Area chart: 10-year projection */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-lg transition-shadow">
            <h4 className="font-semibold text-[#1e3a5f] text-sm mb-1">Projection 10 ans</h4>
            <p className="text-xs text-gray-500 mb-4">Cashflow cumulé + plus-value patrimoine <span className="text-emerald-600 font-semibold">payée par les loyers</span></p>
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <AreaChart data={metrics.projection} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradClassique" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1e3a5f" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#1e3a5f" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradAirbnb" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#c9a227" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#c9a227" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradPatrimoine" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="gradColocation" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#14b8a6" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#14b8a6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis dataKey="année" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip 
                    contentStyle={{ background: '#1e3a5f', border: 'none', borderRadius: 12, color: '#fff', fontSize: 12 }}
                    formatter={(v) => formatEUR(v)}
                  />
                  <Legend 
                    wrapperStyle={{ fontSize: 12, paddingTop: 8, cursor: 'pointer' }} 
                    onClick={(e) => toggleSeries(`area-${e.dataKey}`)}
                    formatter={(value, entry) => (
                      <span style={{ 
                        color: hiddenSeries.has(`area-${entry.dataKey}`) ? '#9ca3af' : '#1e3a5f',
                        textDecoration: hiddenSeries.has(`area-${entry.dataKey}`) ? 'line-through' : 'none',
                        userSelect: 'none'
                      }}>{value}</span>
                    )}
                  />
                  {!hiddenSeries.has('area-Classique') && <Area type="monotone" dataKey="Classique" stroke="#1e3a5f" strokeWidth={2} fill="url(#gradClassique)" />}
                  {!hiddenSeries.has('area-Airbnb') && <Area type="monotone" dataKey="Airbnb" stroke="#c9a227" strokeWidth={2} fill="url(#gradAirbnb)" />}
                  {colocationAvailable && !hiddenSeries.has('area-Colocation') && <Area type="monotone" dataKey="Colocation" stroke="#14b8a6" strokeWidth={2} fill="url(#gradColocation)" />}
                  {!hiddenSeries.has('area-Patrimoine') && <Area type="monotone" dataKey="Patrimoine" stroke="#10b981" strokeWidth={3.5} fill="url(#gradPatrimoine)" />}
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-gray-400 mt-2 italic">
              <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full mr-1"></span>
              Le patrimoine s'apprécie de ~1.5%/an et est <strong className="text-emerald-600">financé par les loyers</strong> sans effort sur votre apport.
            </p>
          </div>
        </div>
      </div>

      {/* VERDICT */}
      <div className="mb-8">
        <p className="text-xs font-bold text-[#1e3a5f] uppercase tracking-[0.15em] mb-3 flex items-center gap-2 border-l-4 border-emerald-500 pl-3">
          <Award size={14} className="text-emerald-600" />
          Notre verdict
        </p>
        <div className="rounded-3xl p-6 md:p-8 relative overflow-hidden bg-gradient-to-br from-[#1e3a5f] via-[#2a4a73] to-[#0f1d2f] text-white">
          {/* Decorative orbs */}
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#c9a227]/20 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-emerald-400/10 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative grid md:grid-cols-3 gap-6 items-center">
            {/* Left: Score gauge — score number in GREEN */}
            <div className="flex flex-col items-center md:items-start">
              <div className="relative w-32 h-32 mb-2">
                <svg viewBox="0 0 120 120" className="transform -rotate-90 w-full h-full">
                  <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="10" />
                  <circle
                    cx="60"
                    cy="60"
                    r="52"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeDasharray={`${(metrics.verdict.score / 100) * 326.7} 326.7`}
                    style={{ filter: 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.6))' }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <p className="text-5xl font-bold leading-none text-emerald-400" style={{ textShadow: '0 0 20px rgba(16, 185, 129, 0.5)' }}>
                    {metrics.verdict.score}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider opacity-70 mt-1">/ 100</p>
                </div>
              </div>
              <p className="text-xs uppercase tracking-[0.2em] opacity-80 font-bold">Score Investisseur</p>
            </div>

            {/* Center: Verdict text */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-md flex items-center justify-center">
                  <metrics.verdict.icon size={26} />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.25em] opacity-80">Recommandation</p>
                  <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold">
                    {metrics.verdict.title}
                  </h3>
                </div>
              </div>
              <p className="text-sm md:text-base leading-relaxed mb-3 opacity-95">
                {metrics.verdict.reason}
              </p>
              <div className="bg-white/15 backdrop-blur-md rounded-xl p-3 flex items-start gap-2 border border-white/20">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5 opacity-90" />
                <p className="text-xs leading-relaxed opacity-95">
                  <strong>À vérifier :</strong> {metrics.verdict.caveat}
                </p>
              </div>

              {/* Quick decision row */}
              <div className="flex flex-wrap gap-2 mt-4">
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
                  metrics.verdict.recommendation === 'airbnb' 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-white/15 text-white/70 border border-white/20'
                }`}>
                  {metrics.verdict.recommendation === 'airbnb' ? <ThumbsUp size={12} /> : <ThumbsDown size={12} />}
                  Airbnb
                </span>
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
                  metrics.verdict.recommendation === 'classique' 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-white/15 text-white/70 border border-white/20'
                }`}>
                  {metrics.verdict.recommendation === 'classique' ? <ThumbsUp size={12} /> : <ThumbsDown size={12} />}
                  Location classique
                </span>
                {colocationAvailable && (
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
                    metrics.verdict.recommendation === 'colocation' 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-white/15 text-white/70 border border-white/20'
                  }`}>
                    {metrics.verdict.recommendation === 'colocation' ? <ThumbsUp size={12} /> : <ThumbsDown size={12} />}
                    Colocation
                  </span>
                )}
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
                  metrics.verdict.recommendation === 'mixte' 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-white/15 text-white/70 border border-white/20'
                }`}>
                  {metrics.verdict.recommendation === 'mixte' ? <ThumbsUp size={12} /> : <ThumbsDown size={12} />}
                  Stratégie mixte
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Insights */}
      <div>
        <p className="text-xs font-bold text-[#1e3a5f] uppercase tracking-[0.15em] mb-3 flex items-center gap-2 border-l-4 border-[#c9a227] pl-3">
          <Sparkles size={14} className="text-[#c9a227]" />
          Analyse &amp; recommandations
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          {metrics.marketDiff < -5 && (
            <InsightCard type="success" icon={CheckCircle2} title="Prix sous le marché">
              Le bien est <strong>{Math.abs(metrics.marketDiff).toFixed(1)}% sous</strong> le prix moyen du secteur. Potentiel de plus-value immédiate.
            </InsightCard>
          )}
          {metrics.marketDiff > 5 && (
            <InsightCard type="warning" icon={AlertCircle} title="Prix au-dessus du marché">
              Le bien est <strong>+{metrics.marketDiff.toFixed(1)}%</strong> au-dessus du prix moyen du secteur. À négocier ou compenser par un fort potentiel locatif.
            </InsightCard>
          )}
          {metrics.grossRoi >= 7 && (
            <InsightCard type="gold" icon={TrendingUp} title="Rendement excellent">
              ROI brut de <strong>{formatPct(metrics.grossRoi)}</strong> : nettement au-dessus de la moyenne nationale (3 à 5%). Opportunité rare.
            </InsightCard>
          )}
          {metrics.grossRoi < 4 && metrics.grossRoi > 0 && (
            <InsightCard type="warning" icon={AlertCircle} title="Rendement modeste">
              ROI brut de <strong>{formatPct(metrics.grossRoi)}</strong> : à compenser par plus-value attendue ou stratégie LCD/Airbnb.
            </InsightCard>
          )}
          {metrics.cashflowBrut !== undefined && false && (
            <InsightCard type="success" icon={CheckCircle2} title="">{null}</InsightCard>
          )}
          {/* leverage card removed */}
          {metrics.airbnbAnnualNet > metrics.annualRent * 1.2 && metrics.annualRent > 0 && (
            <InsightCard type="gold" icon={Sparkles} title="Airbnb très rentable">
              Stratégie courte durée potentiellement <strong>+{formatPct(((metrics.airbnbAnnualNet - metrics.annualRent) / metrics.annualRent) * 100)}</strong> vs location classique. Vérifiez la réglementation locale.
            </InsightCard>
          )}
          {colocationAvailable && metrics.colocationAdvantage > 30 && (
            <InsightCard type="success" icon={Users} title="Colocation rentable">
              <strong>{inputs.roomCount} chambres</strong> à {formatEUR(inputs.pricePerRoom)}/mois génèrent +<strong>{metrics.colocationAdvantage.toFixed(0)}%</strong> vs location classique. Excellente stratégie pour ce bien.
            </InsightCard>
          )}
          {/* leverage card removed - apport n'est plus demandé */}
          {inputs.works > 0 && (
            <InsightCard type="info" icon={Wrench} title="Travaux & fiscalité">
              <strong>{formatEUR(inputs.works)}</strong> de travaux : déductibles fiscalement en régime réel LMNP (BIC) ou foncier (revenus fonciers). Optimisation à étudier.
            </InsightCard>
          )}
          {metrics.priceRentRatio > 250 && metrics.priceRentRatio > 0 && (
            <InsightCard type="warning" icon={AlertCircle} title="Ratio prix/loyer élevé">
              Ratio de <strong>{metrics.priceRentRatio.toFixed(0)}</strong> : il faut {Math.round(metrics.priceRentRatio / 12)} ans de loyer pour rembourser. Vise plutôt &lt; 200.
            </InsightCard>
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <p className="text-[10px] text-gray-400 mt-6 text-center italic">
        Simulation indicative à but informatif. Les valeurs réelles dépendent du marché, de la fiscalité, du financement et de votre situation personnelle.
        Pour une étude personnalisée, contactez-nous.
      </p>
      </div>
    </div>
  );
};

export default InvestorReport;
